import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./icons.js";
/**
 * The page's scroll, locked while any sheet or panel is open (17/10/26). Counted: a sheet opening as another closes
 * (product → report a problem) used to "put back" the hidden scroll it found, so the page stayed stuck until a reload.
 * Every overlay calls this and the page scrolls again only when the last one has gone.
 */
let locks = 0;
let before = "";
export function lockScroll() {
    const html = document.documentElement;
    if (locks === 0) {
        before = html.style.overflow === "hidden" ? "" : html.style.overflow;
        html.style.overflow = "hidden";
    }
    locks++;
    let done = false;
    return () => {
        if (done)
            return;
        done = true;
        locks = Math.max(0, locks - 1);
        if (locks === 0)
            html.style.overflow = before;
    };
}
/**
 * A sheet that slides up from the bottom (a centred panel on a bigger screen): grab handle, title, round close button.
 * Drag it down, tap outside or press Escape to close. It slides away rather than vanishing, and the page behind dims
 * and stops scrolling while it's open.
 *
 * Typed-in sheets don't close by accident (Jamie 29/10/26: "very easy to accidentally click out of and lose all the
 * data you input"): once a field inside has been typed in or changed, tapping outside, Escape, dragging it down or
 * the round close button ask first ("Keep editing" / "Close without saving"), inside the sheet. A tap outside only
 * counts when it also started outside (selecting text and letting go past the edge used to close it). The sheet's own
 * buttons (Save, Cancel…) close it as before. Search boxes and fields marked `data-no-guard` don't count.
 * RowSheet has its own question, so it passes `guard={false}`.
 */
/** `side`: on a computer it slides in from the right, full height, square-edged (Jamie 30/10/26: edit in a slide-in, not a
 * box in the middle); on a phone it comes up from the bottom as every sheet does. */
export function Sheet({ open, onClose, title, children, wide, foot, guard = true, side }) {
    const [shown, setShown] = useState(open);
    const [typed, setTyped] = useState(false);
    const [asking, setAsking] = useState(false);
    const downOutside = useRef(false);
    const stay = useRef(null);
    const [leaving, setLeaving] = useState(false);
    const panel = useRef(null);
    const drag = useRef(null);
    useEffect(() => {
        if (open) {
            setShown(true);
            setLeaving(false);
            setTyped(false);
            setAsking(false);
            return;
        }
        if (!shown)
            return;
        setLeaving(true);
        const t = window.setTimeout(() => { setShown(false); setLeaving(false); }, 260);
        return () => window.clearTimeout(t);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
    // The latest onClose, without re-running the effect below on every render (which stole focus from whatever
    // field you were typing in, so only one letter went in).
    // Closing by accident (outside, Escape, drag, the round button) asks first once something has been typed.
    const tryClose = () => { if (guard && typed)
        setAsking(true);
    else
        onClose(); };
    const close = useRef(tryClose);
    close.current = tryClose;
    useEffect(() => { if (asking) {
        stay.current?.focus({ preventScroll: true });
        stay.current?.scrollIntoView({ block: "nearest" });
    } }, [asking]);
    const onInput = (e) => {
        const t = e.target;
        if (!guard || typed || !t.matches?.("input, select, textarea, [contenteditable=true]"))
            return;
        if (t.matches("[type=search], [data-no-guard], [data-no-guard] *"))
            return;
        setTyped(true);
    };
    useEffect(() => {
        if (!shown)
            return;
        const key = (e) => { if (e.key === "Escape")
            close.current(); };
        document.addEventListener("keydown", key);
        const unlock = lockScroll();
        // Focus the sheet once when it opens, unless a field inside it has already taken focus (autoFocus).
        const t = window.setTimeout(() => { if (!panel.current?.contains(document.activeElement))
            panel.current?.focus({ preventScroll: true }); }, 30);
        return () => { window.clearTimeout(t); document.removeEventListener("keydown", key); unlock(); };
    }, [shown]);
    if (!shown)
        return null;
    const start = (y) => { if ((panel.current?.scrollTop ?? 0) <= 0)
        drag.current = { y, dy: 0 }; };
    const move = (y) => {
        const d = drag.current;
        if (!d || !panel.current)
            return;
        d.dy = Math.max(0, y - d.y);
        panel.current.style.transition = "none";
        panel.current.style.transform = `translateY(${d.dy}px)`;
    };
    const end = () => {
        const d = drag.current;
        drag.current = null;
        if (!d || !panel.current)
            return;
        panel.current.style.transition = "";
        panel.current.style.transform = "";
        if (d.dy > 110)
            close.current();
    };
    return createPortal(_jsx("div", { className: `tc-sh-wrap${side ? " side" : ""}${leaving ? " out" : ""}`, onPointerDown: (e) => { downOutside.current = e.target === e.currentTarget; }, onClick: (e) => { if (e.target === e.currentTarget && downOutside.current)
            close.current(); downOutside.current = false; }, children: _jsxs("div", { ref: panel, tabIndex: -1, className: `tc-sh${wide ? " wide" : ""}${side ? " side" : ""}`, role: "dialog", "aria-modal": "true", "aria-label": typeof title === "string" ? title : undefined, onClick: (e) => e.stopPropagation(), onInput: onInput, onChange: onInput, onTouchStart: (e) => start(e.touches[0].clientY), onTouchMove: (e) => move(e.touches[0].clientY), onTouchEnd: end, children: [_jsxs("div", { className: "tc-sh-head", children: [_jsx("span", { className: "tc-sh-grab", "aria-hidden": "true" }), _jsx("h2", { children: title }), _jsx("button", { className: "tc-sh-x", "aria-label": "Close", onClick: () => close.current(), children: _jsx(Icon, { name: "x", size: 18 }) })] }), _jsx("div", { className: "tc-sh-body", children: children }), asking && (_jsxs("div", { className: "tc-rs-ask tc-sh-ask", role: "alertdialog", "aria-label": "Changes not saved", children: [_jsx("span", { children: "What you've typed isn't saved." }), _jsxs("span", { className: "tc-rs-ask-b", children: [_jsx("button", { ref: stay, type: "button", className: "tc-btn", onClick: () => setAsking(false), children: "Keep editing" }), _jsx("button", { type: "button", className: "tc-btn tc-rs-drop", onClick: () => { setAsking(false); onClose(); }, children: "Close without saving" })] })] })), foot && _jsx("div", { className: "tc-sh-foot", children: foot })] }) }), document.body);
}
/** The friendly "nothing here" inside a sheet: a round picture, a heading and a line. */
export function AllClear({ icon = "inbox", title, children }) {
    return (_jsxs("div", { className: "tc-allclear", children: [_jsx("span", { className: "tc-allclear-i", children: _jsx(Icon, { name: icon, size: 34 }) }), _jsx("b", { children: title }), children && _jsx("p", { children: children })] }));
}
